import React from 'react';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import ChevronRightIcon from '~/components/icons/ChevronRightIcon';
import InlineIconButton from '~/components/InlineIconButton';

import AgentConfigChip, { AGENT_CHIP_TONE } from './AgentConfigChip';
import copies from './copies';
import type { AiExpertInfo, AiExpertScopeConfigurationInfo, ConductorConfigurationModel, ScopeRow } from './types';
import { isExpertScopeEnabled, isScopeConfigurableExpert } from './types';

interface AgentConfigScopeTableProps {
  rows: ScopeRow[];
  /**
   * The organization's configured scopes, from one request rather than one per row. A scope
   * absent from this list has no configuration, which reads the same as disabled: off.
   */
  configurations: ConductorConfigurationModel[];
  /** The organization's expert catalogue; `isScopeConfigurableExpert` decides which are counted. */
  experts: AiExpertInfo[];
  /**
   * Every expert row the organization has, again from one request rather than one per scope. A
   * scope with no row for an expert does not run it, which is the same as a row with both modes
   * off.
   */
  expertScopes: AiExpertScopeConfigurationInfo[];
  onConfigure: (scope: ScopeRow) => void;
}

// SortableTable keys rows by `row.id`, so each (sub-org, lob) scope needs a stable identity.
// `scope` keeps the original ScopeRow so the row callback receives it verbatim.
interface ScopeTableRow extends ScopeRow {
  id: string;
  scope: ScopeRow;
  conductorEnabled: boolean;
  expertsEnabled: number;
}

// Row identity, and the key every join below is keyed on.
const scopeRowId = (subOrganizationId: number | null | undefined, lob: string): string =>
  `${subOrganizationId ?? null}:${lob}`;

const AgentConfigScopeTable: React.FC<AgentConfigScopeTableProps> = ({
  rows,
  configurations,
  experts,
  expertScopes,
  onConfigure,
}) => {
  const enabledScopeIds = new Set(
    configurations
      .filter((configuration) => configuration.is_enabled)
      .map((configuration) => scopeRowId(configuration.sub_organization_id, configuration.lob))
  );

  // The configurable set bounds the count from both ends: a row for an expert this screen does
  // not offer is not counted either, or `n` would climb above `m` -- which is what a stored row
  // for a demo expert, or for one switched off cross-organization, would otherwise do.
  const configurableExpertKeys = new Set(experts.filter(isScopeConfigurableExpert).map((expert) => expert.key));

  const enabledExpertCounts = new Map<string, number>();
  expertScopes.forEach((expertScope) => {
    if (!configurableExpertKeys.has(expertScope.expert_key) || !isExpertScopeEnabled(expertScope)) {
      return;
    }
    const id = scopeRowId(expertScope.sub_organization_id, expertScope.lob);
    enabledExpertCounts.set(id, (enabledExpertCounts.get(id) ?? 0) + 1);
  });

  const tableRows: ScopeTableRow[] = rows.map((row) => ({
    ...row,
    id: scopeRowId(row.sub_organization_id, row.lob),
    scope: row,
    conductorEnabled: enabledScopeIds.has(scopeRowId(row.sub_organization_id, row.lob)),
    expertsEnabled: enabledExpertCounts.get(scopeRowId(row.sub_organization_id, row.lob)) ?? 0,
  }));

  const columns = [
    { id: 'sub_organization_name', label: copies.scopeTable.subOrganizationColumn, numeric: false },
    { id: 'lob', label: copies.scopeTable.lobColumn, numeric: false },
    {
      id: 'conductor',
      label: copies.scopeTable.conductorColumn,
      disableSort: true,
      specialCell: (row: ScopeTableRow) => (
        <AgentConfigChip
          label={row.conductorEnabled ? copies.scopeTable.conductorOn : copies.scopeTable.conductorOff}
          tone={row.conductorEnabled ? AGENT_CHIP_TONE.ON : AGENT_CHIP_TONE.NEUTRAL}
        />
      ),
    },
    {
      id: 'experts',
      label: copies.scopeTable.expertsColumn,
      disableSort: true,
      specialCell: (row: ScopeTableRow) =>
        copies.scopeTable.expertsCount(row.expertsEnabled, configurableExpertKeys.size),
    },
    {
      id: 'actions',
      label: copies.scopeTable.actionColumn,
      disableSort: true,
      // Right-aligned so the chevron sits against the table edge rather than floating in the
      // middle of the last column, matching the integrations admin tables' actions column.
      align: 'right',
      specialCell: (row: ScopeTableRow) => (
        <InlineIconButton
          icon={ChevronRightIcon}
          tooltipTitle={copies.scopeTable.actionColumn}
          onClick={() => onConfigure(row.scope)}
        />
      ),
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={tableRows}
      // SortableTable hands the handler the row's *id* alone -- see
      // `SortableTableCell.handleCellClick` -- so the scope is looked up by it. Written as
      // `(event, row)` this threw on every row click, because there is no second argument.
      onRowClick={(rowId: string) => {
        const clicked = tableRows.find((row) => row.id === rowId);
        if (clicked) onConfigure(clicked.scope);
      }}
      emptyStateComponent={
        <EmptyState title={copies.scopeTable.emptyTitle} subtitle={copies.scopeTable.emptySubtitle} />
      }
    />
  );
};

export default AgentConfigScopeTable;
